/**
 * Referral Tracking Utility
 * Multi-layer tracking to protect marketer commissions
 * 
 * Layers:
 * 1. Cookies (30 days) - Most persistent
 * 2. localStorage - Backup
 * 3. Device Fingerprint - Unique device identifier
 */

import { safeLocalStorage } from './safeStorage';

const COOKIE_NAME = 'oeia_ref_code';
const COOKIE_DAYS = 30;
const STORAGE_KEY = 'pending_referral_code';
const FINGERPRINT_KEY = 'oeia_device_fp';
const REFERRAL_TIMESTAMP_KEY = 'oeia_ref_timestamp';

/**
 * Generate a simple device fingerprint
 * Combines multiple browser/device properties to create a unique identifier
 */
export const generateDeviceFingerprint = () => {
  const components = [
    navigator.userAgent,
    navigator.language,
    screen.width + 'x' + screen.height,
    screen.colorDepth,
    new Date().getTimezoneOffset(),
    navigator.hardwareConcurrency || 'unknown',
    navigator.platform,
    // Canvas fingerprint
    getCanvasFingerprint(),
  ];
  
  // Create a hash from components
  const fingerprint = components.join('|||');
  return hashCode(fingerprint);
};

/**
 * Canvas fingerprint - unique per device/browser
 */
const getCanvasFingerprint = () => {
  try {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    ctx.textBaseline = 'top';
    ctx.font = '14px Arial';
    ctx.fillText('oeia.io fingerprint', 2, 2);
    return canvas.toDataURL().slice(-50);
  } catch (e) {
    return 'canvas-not-supported';
  }
};

/**
 * Simple hash function
 */
const hashCode = (str) => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32bit integer
  }
  return 'FP' + Math.abs(hash).toString(36).toUpperCase();
};

/**
 * Set cookie with expiration
 */
export const setCookie = (name, value, days) => {
  const expires = new Date();
  expires.setTime(expires.getTime() + (days * 24 * 60 * 60 * 1000));
  document.cookie = `${name}=${value};expires=${expires.toUTCString()};path=/;SameSite=Lax`;
};

/**
 * Get cookie by name
 */
export const getCookie = (name) => {
  const nameEQ = name + '=';
  const ca = document.cookie.split(';');
  for (let i = 0; i < ca.length; i++) {
    let c = ca[i];
    while (c.charAt(0) === ' ') c = c.substring(1, c.length);
    if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
  }
  return null;
};

/**
 * Delete cookie
 */
export const deleteCookie = (name) => {
  document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=/;`;
};

/**
 * Save referral code with multi-layer persistence
 * Call this when user visits with ?ref=CODE
 */
export const saveReferralCode = (code) => {
  if (!code) return;
  
  const upperCode = code.toUpperCase();
  const timestamp = Date.now();
  const fingerprint = generateDeviceFingerprint();
  
  // Layer 1: Cookie (30 days)
  setCookie(COOKIE_NAME, upperCode, COOKIE_DAYS);
  
  // Layer 2: localStorage
  safeLocalStorage.setItem(STORAGE_KEY, upperCode);
  safeLocalStorage.setItem(REFERRAL_TIMESTAMP_KEY, timestamp.toString());
  
  // Layer 3: Store fingerprint association
  safeLocalStorage.setItem(FINGERPRINT_KEY, fingerprint);
  
  // Also store in sessionStorage for current session
  try {
    sessionStorage.setItem(STORAGE_KEY, upperCode);
  } catch (e) {}
  
  console.log(`[Referral Tracking] Saved code: ${upperCode}, Fingerprint: ${fingerprint}`);
  
  return { code: upperCode, fingerprint, timestamp };
};

/**
 * Get referral code from any available source
 * Priority: Cookie > localStorage > sessionStorage
 */
export const getReferralCode = () => {
  // Priority 1: Cookie (most persistent)
  const cookieCode = getCookie(COOKIE_NAME);
  if (cookieCode) {
    console.log('[Referral Tracking] Found code in cookie:', cookieCode);
    return cookieCode;
  }
  
  // Priority 2: localStorage
  const localCode = safeLocalStorage.getItem(STORAGE_KEY);
  if (localCode) {
    console.log('[Referral Tracking] Found code in localStorage:', localCode);
    // Restore to cookie if missing
    setCookie(COOKIE_NAME, localCode, COOKIE_DAYS);
    return localCode;
  }
  
  // Priority 3: sessionStorage
  try {
    const sessionCode = sessionStorage.getItem(STORAGE_KEY);
    if (sessionCode) {
      console.log('[Referral Tracking] Found code in sessionStorage:', sessionCode);
      return sessionCode;
    }
  } catch (e) {}
  
  return null;
};

/**
 * Get referral data including fingerprint
 */
export const getReferralData = () => {
  const code = getReferralCode();
  const fingerprint = safeLocalStorage.getItem(FINGERPRINT_KEY) || generateDeviceFingerprint();
  const timestamp = safeLocalStorage.getItem(REFERRAL_TIMESTAMP_KEY);
  
  return {
    code,
    fingerprint,
    timestamp: timestamp ? parseInt(timestamp) : null,
    hasReferral: !!code
  };
};

/**
 * Clear referral data after successful tracking
 */
export const clearReferralCode = () => {
  deleteCookie(COOKIE_NAME);
  safeLocalStorage.removeItem(STORAGE_KEY);
  safeLocalStorage.removeItem(REFERRAL_TIMESTAMP_KEY);
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch (e) {}
  // Keep fingerprint for future tracking
  console.log('[Referral Tracking] Cleared referral code');
};

/**
 * Check if referral code is still valid (within 30 days)
 */
export const isReferralValid = () => {
  const timestamp = safeLocalStorage.getItem(REFERRAL_TIMESTAMP_KEY);
  if (!timestamp) return true; // No timestamp means we rely on cookie expiration
  
  const daysSince = (Date.now() - parseInt(timestamp)) / (1000 * 60 * 60 * 24);
  return daysSince <= COOKIE_DAYS;
};

/**
 * Initialize referral tracking from URL
 * Call this on app initialization
 */
export const initReferralTracking = () => {
  const urlParams = new URLSearchParams(window.location.search);
  const refCode = urlParams.get('ref');
  
  if (refCode) {
    saveReferralCode(refCode);
    
    // Clean URL without losing the referral (optional)
    // This keeps the URL clean but preserves tracking
    // window.history.replaceState({}, '', window.location.pathname);
  }
  
  return getReferralData();
};

export default {
  saveReferralCode,
  getReferralCode,
  getReferralData,
  clearReferralCode,
  isReferralValid,
  initReferralTracking,
  generateDeviceFingerprint
};
